'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Check, X, Eye, MoreHorizontal, ChevronUp, ChevronDown, ChevronsUpDown, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

type BookingStatus = 'confirmed' | 'pending' | 'cancelled' | 'checked-in' | 'checked-out';

interface Booking {
  id: string;
  guestName: string;
  guestEmail: string;
  apartment: string;
  aptShort: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  guests: number;
  total: number;
  status: BookingStatus;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  bookedOn: string;
}

// BACKEND INTEGRATION: GET /api/admin/bookings
const allBookings: Booking[] = [
  { id: 'bk-2026-0041', guestName: 'Sophie Marchand', guestEmail: 'sophie.m@gmail.com', apartment: 'Sunset Terrace Suite', aptShort: 'Suite', checkIn: '18 May 2026', checkOut: '23 May 2026', nights: 5, guests: 2, total: 835, status: 'confirmed', paymentStatus: 'paid', bookedOn: '02 May 2026' },
  { id: 'bk-2026-0042', guestName: 'Lars Eriksson', guestEmail: 'l.eriksson@outlook.se', apartment: 'Rooftop Haven', aptShort: 'Roof', checkIn: '20 May 2026', checkOut: '27 May 2026', nights: 7, guests: 2, total: 1400, status: 'pending', paymentStatus: 'pending', bookedOn: '10 May 2026' },
  { id: 'bk-2026-0043', guestName: 'Giulia Ferretti', guestEmail: 'giulia.f@hotmail.it', apartment: 'Garden Villa Escape', aptShort: 'Villa', checkIn: '22 May 2026', checkOut: '29 May 2026', nights: 7, guests: 5, total: 1715, status: 'pending', paymentStatus: 'pending', bookedOn: '11 May 2026' },
  { id: 'bk-2026-0044', guestName: 'James Whitfield', guestEmail: 'j.whitfield@gmail.com', apartment: 'Ocean Breeze Loft', aptShort: 'Loft', checkIn: '14 May 2026', checkOut: '17 May 2026', nights: 3, guests: 2, total: 369, status: 'checked-in', paymentStatus: 'paid', bookedOn: '28 Apr 2026' },
  { id: 'bk-2026-0045', guestName: 'Annika Weber', guestEmail: 'annika.w@web.de', apartment: 'Sunset Terrace Suite', aptShort: 'Suite', checkIn: '28 May 2026', checkOut: '04 Jun 2026', nights: 7, guests: 3, total: 1225, status: 'confirmed', paymentStatus: 'paid', bookedOn: '05 May 2026' },
  { id: 'bk-2026-0046', guestName: 'Matteo Conti', guestEmail: 'matteo.c@libero.it', apartment: 'Garden Villa Escape', aptShort: 'Villa', checkIn: '03 May 2026', checkOut: '10 May 2026', nights: 7, guests: 4, total: 1715, status: 'checked-out', paymentStatus: 'paid', bookedOn: '15 Apr 2026' },
  { id: 'bk-2026-0047', guestName: 'Ingrid Holst', guestEmail: 'ingrid.h@gmail.com', apartment: 'Rooftop Haven', aptShort: 'Roof', checkIn: '01 Jun 2026', checkOut: '08 Jun 2026', nights: 7, guests: 2, total: 1400, status: 'pending', paymentStatus: 'pending', bookedOn: '13 May 2026' },
  { id: 'bk-2026-0048', guestName: 'Carlos Ruiz', guestEmail: 'carlos.r@gmail.com', apartment: 'Ocean Breeze Loft', aptShort: 'Loft', checkIn: '25 Apr 2026', checkOut: '28 Apr 2026', nights: 3, guests: 1, total: 349, status: 'checked-out', paymentStatus: 'paid', bookedOn: '10 Apr 2026' },
  { id: 'bk-2026-0049', guestName: 'Nadia Kowalski', guestEmail: 'nadia.k@wp.pl', apartment: 'Sunset Terrace Suite', aptShort: 'Suite', checkIn: '10 Jun 2026', checkOut: '17 Jun 2026', nights: 7, guests: 4, total: 1225, status: 'confirmed', paymentStatus: 'paid', bookedOn: '14 May 2026' },
  { id: 'bk-2026-0050', guestName: 'Pierre Dubois', guestEmail: 'p.dubois@orange.fr', apartment: 'Garden Villa Escape', aptShort: 'Villa', checkIn: '12 Jun 2026', checkOut: '19 Jun 2026', nights: 7, guests: 6, total: 1715, status: 'confirmed', paymentStatus: 'paid', bookedOn: '14 May 2026' },
  { id: 'bk-2026-0038', guestName: 'Hana Novak', guestEmail: 'hana.n@seznam.cz', apartment: 'Rooftop Haven', aptShort: 'Roof', checkIn: '05 May 2026', checkOut: '09 May 2026', nights: 4, guests: 2, total: 800, status: 'cancelled', paymentStatus: 'refunded', bookedOn: '20 Apr 2026' },
  { id: 'bk-2026-0039', guestName: 'Tom Bakker', guestEmail: 't.bakker@gmail.com', apartment: 'Ocean Breeze Loft', aptShort: 'Loft', checkIn: '01 May 2026', checkOut: '05 May 2026', nights: 4, guests: 2, total: 467, status: 'checked-out', paymentStatus: 'paid', bookedOn: '12 Apr 2026' },
];

type SortKey = 'checkIn' | 'total' | 'nights' | 'guestName';
type SortDir = 'asc' | 'desc';

const paymentBadge: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  refunded: 'bg-gray-100 text-gray-600',
};

export default function BookingsTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('checkIn');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [bookings, setBookings] = useState<Booking[]>(allBookings);
  const [actionModal, setActionModal] = useState<{ booking: Booking; action: 'approve' | 'reject' | 'view' } | null>(null);

  const filtered = bookings
    .filter((b) => {
      const q = search.toLowerCase();
      const matchSearch = b.guestName.toLowerCase().includes(q) ||
        b.id.toLowerCase().includes(q) ||
        b.apartment.toLowerCase().includes(q) ||
        b.guestEmail.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || b.status === statusFilter;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'total') cmp = a.total - b.total;
      else if (sortKey === 'nights') cmp = a.nights - b.nights;
      else if (sortKey === 'guestName') cmp = a.guestName.localeCompare(b.guestName);
      else cmp = a.checkIn.localeCompare(b.checkIn);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  const allSelected = paginated.length > 0 && paginated.every((b) => selected.includes(b.id));
  const toggleAll = () => {
    if (allSelected) setSelected((s) => s.filter((id) => !paginated.find((b) => b.id === id)));
    else setSelected((s) => [...new Set([...s, ...paginated.map((b) => b.id)])]);
  };
  const toggleRow = (id: string) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  // BACKEND INTEGRATION: PATCH /api/admin/bookings/:id/status
  const handleApprove = (booking: Booking) => {
    setBookings((bs) => bs.map((b) => b.id === booking.id ? { ...b, status: 'confirmed', paymentStatus: 'paid' } : b));
    setActionModal(null);
    toast.success(`Booking ${booking.id} confirmed for ${booking.guestName}`);
  };

  const handleReject = (booking: Booking) => {
    setBookings((bs) => bs.map((b) => b.id === booking.id ? { ...b, status: 'cancelled', paymentStatus: 'refunded' } : b));
    setActionModal(null);
    toast.error(`Booking ${booking.id} rejected — ${booking.guestName} notified`);
  };

  const bulkReject = () => {
    setBookings((bs) => bs.map((b) => selected.includes(b.id) ? { ...b, status: 'cancelled' } : b));
    toast.error(`${selected.length} booking${selected.length > 1 ? 's' : ''} cancelled`);
    setSelected([]);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-700 text-foreground">All Bookings</h2>
          <p className="text-xs text-muted-foreground">{filtered.length} total · {bookings.filter((b) => b.status === 'pending').length} pending approval</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 pr-4 py-2 bg-muted rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring w-48"
            />
          </div>
          {/* Status filter */}
          <div className="relative">
            <Filter size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="pl-8 pr-3 py-2 bg-muted rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked-in">Checked In</option>
              <option value="checked-out">Checked Out</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="mb-3 bg-foreground text-white rounded-xl px-4 py-3 flex items-center justify-between animate-slide-up">
          <span className="text-sm font-600">{selected.length} booking{selected.length > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={bulkReject} className="flex items-center gap-1.5 text-xs font-600 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors">
              <X size={13} /> Cancel Selected
            </button>
            <button onClick={() => setSelected([])} className="text-xs text-white/60 hover:text-white px-2 py-1.5 rounded-lg transition-colors">
              Deselect
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60 border-b border-border">
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-border accent-primary"
                  />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                  Booking ID
                </th>
                <th
                  className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none"
                  onClick={() => toggleSort('guestName')}
                >
                  <span className="flex items-center gap-1">Guest <SortIcon k="guestName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">Apartment</th>
                <th
                  className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none"
                  onClick={() => toggleSort('checkIn')}
                >
                  <span className="flex items-center gap-1">Check-in <SortIcon k="checkIn" /></span>
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">Check-out</th>
                <th
                  className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none"
                  onClick={() => toggleSort('nights')}
                >
                  <span className="flex items-center gap-1">Nights <SortIcon k="nights" /></span>
                </th>
                <th
                  className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer hover:text-foreground select-none"
                  onClick={() => toggleSort('total')}
                >
                  <span className="flex items-center gap-1">Total <SortIcon k="total" /></span>
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">Payment</th>
                <th className="px-4 py-3 text-left text-[11px] font-700 uppercase tracking-wider text-muted-foreground whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((booking, rowIdx) => (
                <tr
                  key={booking.id}
                  className={`border-b border-border last:border-0 transition-colors hover:bg-muted/40 ${rowIdx % 2 === 0 ? '' : 'bg-muted/20'} ${selected.includes(booking.id) ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(booking.id)}
                      onChange={() => toggleRow(booking.id)}
                      className="rounded border-border accent-primary"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-700 text-muted-foreground tabular-nums font-mono">{booking.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-600 text-foreground text-sm whitespace-nowrap">{booking.guestName}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[160px]">{booking.guestEmail}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-600 text-foreground whitespace-nowrap">{booking.aptShort}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground whitespace-nowrap tabular-nums">{booking.checkIn}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground whitespace-nowrap tabular-nums">{booking.checkOut}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-600 text-foreground tabular-nums">{booking.nights}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-700 text-foreground tabular-nums">€{booking.total.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={booking.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 capitalize ${paymentBadge[booking.paymentStatus]}`}>
                      {booking.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setActionModal({ booking, action: 'approve' })}
                            className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                            title="Approve booking"
                          >
                            <Check size={13} />
                          </button>
                          <button
                            onClick={() => setActionModal({ booking, action: 'reject' })}
                            className="p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                            title="Reject booking"
                          >
                            <X size={13} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setActionModal({ booking, action: 'view' })}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="View booking details"
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="More actions"
                      >
                        <MoreHorizontal size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty state */}
          {paginated.length === 0 && (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">📋</div>
              <h3 className="font-700 text-base text-foreground mb-1">No bookings found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rows per page:</span>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="bg-white border border-border rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {[5, 8, 10, 20].map((n) => (
                  <option key={`perpage-${n}`} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={`page-${i + 1}`}
                  onClick={() => setPage(i + 1)}
                  className={`w-8 h-8 text-xs rounded-lg transition-colors ${page === i + 1 ? 'bg-primary text-white font-700' : 'hover:bg-muted text-muted-foreground border border-border'}`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-card w-full max-w-md rounded-2xl shadow-2xl p-6 animate-slide-up">
            {actionModal.action === 'view' ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-700 text-base text-foreground">Booking Details</h3>
                  <button onClick={() => setActionModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
                    <X size={18} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div className="bg-muted/60 rounded-xl p-4 space-y-2.5">
                    {[
                      { label: 'Booking ID', value: actionModal.booking.id },
                      { label: 'Guest', value: actionModal.booking.guestName },
                      { label: 'Email', value: actionModal.booking.guestEmail },
                      { label: 'Apartment', value: actionModal.booking.apartment },
                      { label: 'Check-in', value: actionModal.booking.checkIn },
                      { label: 'Check-out', value: actionModal.booking.checkOut },
                      { label: 'Nights', value: String(actionModal.booking.nights) },
                      { label: 'Guests', value: String(actionModal.booking.guests) },
                      { label: 'Total', value: `€${actionModal.booking.total.toLocaleString()}` },
                      { label: 'Booked On', value: actionModal.booking.bookedOn },
                    ].map((row) => (
                      <div key={`detail-${row.label}`} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className="font-600 text-foreground text-right">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <StatusBadge status={actionModal.booking.status} />
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-600 capitalize ${paymentBadge[actionModal.booking.paymentStatus]}`}>
                      {actionModal.booking.paymentStatus}
                    </span>
                  </div>
                </div>
                {actionModal.booking.status === 'pending' && (
                  <div className="flex gap-3 mt-5">
                    <button onClick={() => handleApprove(actionModal.booking)} className="flex-1 btn-secondary justify-center text-sm py-2.5">
                      <Check size={15} /> Approve
                    </button>
                    <button onClick={() => handleReject(actionModal.booking)} className="flex-1 bg-red-100 text-red-600 hover:bg-red-200 font-600 text-sm px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                      <X size={15} /> Reject
                    </button>
                  </div>
                )}
                <button onClick={() => setActionModal(null)} className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors py-2">
                  Close
                </button>
              </>
            ) : actionModal.action === 'approve' ? (
              <>
                <div className="text-center mb-5">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check size={28} className="text-green-600" />
                  </div>
                  <h3 className="font-700 text-lg text-foreground">Approve Booking?</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Confirm booking <span className="font-700 text-foreground">{actionModal.booking.id}</span> for{' '}
                    <span className="font-700 text-foreground">{actionModal.booking.guestName}</span>?
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {actionModal.booking.apartment} · {actionModal.booking.checkIn} → {actionModal.booking.checkOut}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setActionModal(null)} className="flex-1 btn-outline text-sm py-2.5 justify-center">Cancel</button>
                  <button onClick={() => handleApprove(actionModal.booking)} className="flex-1 btn-secondary justify-center text-sm py-2.5">
                    <Check size={15} /> Confirm Booking
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-5">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <X size={28} className="text-red-600" />
                  </div>
                  <h3 className="font-700 text-lg text-foreground">Reject Booking?</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Reject booking <span className="font-700 text-foreground">{actionModal.booking.id}</span> for{' '}
                    <span className="font-700 text-foreground">{actionModal.booking.guestName}</span>? This will notify the guest and issue a refund.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setActionModal(null)} className="flex-1 btn-outline text-sm py-2.5 justify-center">Keep Booking</button>
                  <button onClick={() => handleReject(actionModal.booking)} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-600 text-sm px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5 active:scale-95">
                    <X size={15} /> Reject & Refund
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}